import React, { useState } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Icon from 'tbk-components/src/components/Icon';
import { ILink } from 'tbk-components/src/components/Button';

export interface IOption {
	label?: string;
	value?: string;
	image?: IImage;
	/**
	 * @control richtext
	 */
	description?: string;
	/*
	 * The frequencies that are highlighted in the graph below.
	 * Please note the frequencies displayed in the description are editable under the "description" field.
	 */
	highlightedFrequencies?: string[];
	optionalFrequencies?: string[];
}

export interface IAntennaSelectionRadioOptionsProps extends IBaseElementProps {
	topLeftText?: string;
	topRightLink?: ILink;
	options: IOption[];
	updateQuery?: (query: string) => void;
	updateHighlightedF?: (f: string[]) => void;
	updateOptionalF?: (f: string[]) => void;
}

const AntennaSelectionRadioOptions: React.FC<
	IAntennaSelectionRadioOptionsProps
> = ({
	topLeftText,
	topRightLink,
	options,
	updateQuery,
	updateHighlightedF,
	updateOptionalF,
	className,
	classNames = [],
}) => {
	const [choice, setChoice] = useState('');

	const handleChange = (event) => {
		setChoice(event.target.value);
		updateQuery && updateQuery(event.target.value);
	};

	return (
		<div className={className || cn('radio-options', ...classNames)}>
			<div className="radio-options__container">
				{(topLeftText || topRightLink) && (
					<div className="radio-options__top-text">
						{topLeftText && (
							<div className="subheading">{topLeftText}</div>
						)}
						{topRightLink && (
							<a href={topRightLink?.href}>
								{topRightLink?.label}
								<Icon name="new-window" />
							</a>
						)}
					</div>
				)}
				{Array.isArray(options) && options.length > 0 && (
					<div className="radio-options__options">
						{options.map((option, index) => {
							const formattedLabel = option?.label
								.toLocaleLowerCase()
								.replace(/\s/g, '-');
							return (
								<div
									className={cn(
										'radio-options__option',
										choice === option?.value
											? 'selected'
											: '',
									)}
									key={index}
								>
									<label
										htmlFor={formattedLabel}
										className="radio-options__top-part"
									>
										<input
											type="radio"
											name={formattedLabel}
											id={formattedLabel}
											value={option?.value}
											checked={choice === option?.value}
											onChange={(e) => {
												handleChange(e);
												updateHighlightedF &&
													updateHighlightedF(
														option.highlightedFrequencies,
													);
												updateOptionalF &&
													updateOptionalF(
														option.optionalFrequencies,
													);
											}}
										/>
										<span className="radio-options__button"></span>
										<span className="radio-options__label-text">
											{option?.label}
										</span>
									</label>
									<div className="radio-options__bottom-part">
										{option?.image && (
											<Image
												className="radio-options__image"
												{...option.image}
											/>
										)}
										{option?.description && (
											<div
												className="radio-options__description"
												dangerouslySetInnerHTML={{
													__html: option?.description,
												}}
											/>
										)}
									</div>
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
};

export default AntennaSelectionRadioOptions;
